import cx from 'classnames'
import PropTypes from 'prop-types'
import React, { useContext, useRef } from 'react'
import { createSelectable } from 'react-selectable-fast-control'
import * as PT from '~/PropTypes'
import { normalizeThumbnail, refTimeToHHMMSSMSMS } from '~/Util'
import { FILE_STATUS } from '~/enums'
import * as Assets from '~/models/Asset'
import { Folder } from '~/models/Folder'
import ListItem from '../BaseAssetsListItem'
import GroupMediaControls from './GroupMediaControls'
import './ListItem.scss'
import MediaControls from './MediaControls'
import MediaProgress from './MediaProgress'
import ThumbnailsProgress from './ThumbnailsProgress'
import { FolderMediaPreview } from './media-previews/FolderMediaPreview'
// ---
import { TranslationContext } from '~/contexts/TranslationContext'
import { isVoiceover } from '~/Util/recording'
import styles from './listItem.module.scss'
import { isTransitionFile, isTextFile } from '~/helpers/assets/fetchFile'
import { getTransitionItem } from '../Transition/ListItem'

function getThumbnail(asset) {
  if (asset instanceof Assets.AudioAsset) {
    if (isVoiceover(asset)) {
      return (
        <>
          <div className={cx(styles.thumbnailBackground, styles.voiceBackground)} />
          <div className={cx(styles.thumbnailIcon, styles.voiceIcon)} />
        </>
      )
    }

    return (
      <>
        <div className={cx(styles.thumbnailBackground, styles.audioBackground)} />
        <div className={cx(styles.thumbnailIcon, styles.audioIcon)} />
      </>
    )
  }

  return null
}

function MediaAssetsListItem(props) {
  const {
    /** @type {Assets.VideoAsset|Assets.ImageAsset|Assets.AudioAsset} */
    asset,
    // eslint-disable-next-line react/prop-types
    isSelected,
    thumbBorder,
    ...rest
  } = props
  const groupContextMenuRef = useRef(null)

  const isFolder = asset instanceof Folder

  const { t } = useContext(TranslationContext)
  return (
    <ListItem
      {...rest}
      className={cx('media-file', {
        'media-file__prevent-dragging': false,
        'text-asset': isTextFile(asset.id), // for Project Media
        'transition-asset': isTransitionFile(asset.id), // for Project Media
      })}
      isFolder={isFolder}
      isSelected={isSelected}
      renaming={isFolder}
      asset={asset}
      ref={groupContextMenuRef}
      composeDragItem={isTransitionFile(asset.id)
        ? item => ({ transitionType: item.type })
        : undefined}
    >
      <div
        className={cx('media-file__img', { thumbBorder })}
      >
        {/* do not show thumbnail if error happened */}
        <If condition={asset.thumbnail && asset.error === undefined}>
          {getThumbnail(asset) || (
          <img
            src={normalizeThumbnail(asset.thumbnail)}
            className={cx('thumbnail', {
              thumbBorder,
            })}
            alt={`${t('MEDIA_TEXT_LOADING')}...`}
          />
          )}
        </If>
        <If condition={asset.text}>
          {asset.text}
        </If>
        <If condition={isTransitionFile(asset.id)}>
          <div className="transition-asset__item" style={{ position: 'relative', pointerEvents: 'none' }}>
            {getTransitionItem(asset.type)}
          </div>
        </If>
        <If condition={isFolder}>
          <FolderMediaPreview />
        </If>

        <If condition={!(asset instanceof Assets.ImageAsset) && asset.status === FILE_STATUS.READY}>
          <div className="media-file__duration">
            {refTimeToHHMMSSMSMS(asset.duration)}
          </div>
        </If>

        <Choose>
          <When condition={asset.error !== undefined}>
            <div className="media-file__error">
              <div className="media-file__error-message">
                {t(`MEDIA_ERROR_${asset.error.type}`)}
              </div>
            </div>
          </When>
          <When condition={!asset.status || asset.status === FILE_STATUS.READY}>
            {/* no progress should be displayed */}
          </When>
          <When condition={asset.status === FILE_STATUS.PREPARE_THUMBNAILS}>
            <ThumbnailsProgress progress={asset.progress} />
          </When>
          <Otherwise>
            <MediaProgress
              progress={asset.progress}
              status={asset.status}
              endless={(asset.status === FILE_STATUS.IMPORT && asset.progress === 0)
                || (asset.status === FILE_STATUS.MEDIAINFO)}
            />
          </Otherwise>
        </Choose>
        <If condition={isSelected && !isFolder}>
          <GroupMediaControls
            anchorEl={groupContextMenuRef.current}
            asset={asset}
            isFolder={isFolder}
          />
        </If>
        <MediaControls
          item={asset}
          isFolder={isFolder}
        />
      </div>
    </ListItem>
  )
}

export default createSelectable(MediaAssetsListItem)

MediaAssetsListItem.defaultProps = {
  thumbBorder: false,
}

MediaAssetsListItem.propTypes = {
  asset: PropTypes.oneOfType([ PT.VideoAsset, PT.ImageAsset, PT.AudioAsset, PT.Folder ]).isRequired,
  thumbBorder: PropTypes.bool,
}
